// @flow

import isEqual from 'lodash/isEqual';
import queryString from 'query-string';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useRouteMatch } from 'react-router-dom';
import { Section, Wrapper } from '@riseart/layout';
import { gridSectionCls } from '@riseart/grid';
import {
  pagination as PAGINATION_ENUM,
  filter as FILTER_ENUM,
  meta as META_ENUM,
  search as ENUM_SEARCH,
} from 'Enum';
import { useQuery } from 'shared_services/apollo/useQuery';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { selectByStoreKey } from 'shared_services/redux/selectors/byKey';
import { selectAuthShippingCountryCode } from 'shared_services/redux/selectors/auth';
import { useStoreCode } from 'shared_services/redux/hooks/useStoreCode';
import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { LDTYPES, generateLDJSON } from 'shared_models/seo/ldJSON';
import { HOCFilterContainer } from 'shared_hocs/filters/Container';
import { filterDomainToStoreKey } from 'shared_hocs/filters/withFilterState';
import { MetaCategoryContainer } from 'shared_hocs/meta/categories/Category';
import { ArtistList as ArtistListSection } from 'shared_components/artist/list/List';
import { ArtistFiltersList } from 'shared_components/artist/containers/filters/Artist';
import { FilterSection } from 'shared_components/common/filters/container/Section';
import HOCFilterPageRoute from 'shared_hocs/routes/FilterPageRoute';
import SEARCH_ARTISTS_QUERY from 'shared_data/queries/search/artists.graphql';
import {
  convertPayloadToQueryVars,
  filterSearchTextRouteHandler,
} from 'shared_services/riseart/utils/Utils';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { SearchForm } from 'shared_components/forms/search/Form';
import { WithFilterState } from 'shared_hocs/connect/WithFilterState';
import { FilterBreadcrumbs } from 'shared_components/common/filters/breadcrumbs/Breadcrumbs';
import { CreateFollowProvider } from 'shared_data/providers/follow/CreateFollow';

import { artistListFilterSectionCls } from 'shared_components/pages/artist/List.less';
import { searchFormSectionCls } from 'shared_components/forms/search/Form.less';

// HOCs
const HOCArtistListFiltersContainer = HOCFilterContainer(ArtistFiltersList, {
  domain: FILTER_ENUM.domain.ARTIST,
  loadIndexedUrlsList: true,
});

/**
 * ArtistList
 *
 * @param {Object} props
 */
const ArtistList = ({ domain, pageUrl, paginationType, location }: Object) => {
  const ITEMS_PER_PAGE = 15;
  const { q } = queryString.parse(location.search);
  const hasTextQuery = q !== undefined;
  const storeCode = useStoreCode();
  const shippingCountryCode = useSelector(selectAuthShippingCountryCode);
  const selectFilterStoreKey = useMemo(
    () => selectByStoreKey(filterDomainToStoreKey[domain]),
    [domain],
  );
  const { filters, page, sort } =
    useSelector(
      selectFilterStoreKey,
      (oldValue, newValue) =>
        isEqual(oldValue.filters, newValue.filters) &&
        oldValue.page === newValue.page &&
        oldValue.sort === newValue.sort,
    ) || {};
  const { storeCode: code, ...inputArtistFilter } = useMemo(
    () =>
      convertPayloadToQueryVars({
        items: ITEMS_PER_PAGE,
        page,
        sort,
        artworks: true,
        ...filters,
      }),
    [page, sort, filters],
  );
  const { loading, data, error } = useQuery(SEARCH_ARTISTS_QUERY, {
    variables: {
      inputArtistFilter,
      ...(shippingCountryCode ? { rankingCountry: shippingCountryCode } : null),
      ...(q ? { textQuery: q } : null),
    },
    skip: !storeCode,
  });
  const hasError = !!error || !data;
  const { items, pagination } = (data && data.searchArtists) || {};

  return (
    <LayoutDefault>
      {hasTextQuery ? (
        <Section background="white" padding="none" className={searchFormSectionCls}>
          <Wrapper>
            <WithFilterState domain={domain}>
              {(filters) => (
                <FilterBreadcrumbs
                  filterDomain={domain}
                  filters={filters}
                  config={['medium', 'subMedium', 'style', 'subject', 'subSubject']}
                />
              )}
            </WithFilterState>
            <SearchForm
              allowTracking
              title={
                <FormattedMessage id="forms.search.yourArtistSearchResults">
                  {(title: string) => title}
                </FormattedMessage>
              }
              urlRouteHandler={filterSearchTextRouteHandler}
              autoFocus
              enableClear
              showSuggestions
              excludedParameters={['page']}
              displayStyle="horizontal"
              domain={ENUM_SEARCH.domain.ARTIST}
            />
          </Wrapper>
        </Section>
      ) : null}
      <FilterSection topPadded={hasTextQuery} className={artistListFilterSectionCls}>
        {!hasTextQuery ? (
          <MetaCategoryContainer
            domain={domain}
            config={['medium', 'subMedium', 'style', 'region', 'born']}
          />
        ) : null}
        <HOCArtistListFiltersContainer domain={domain} />
      </FilterSection>
      <Section className={gridSectionCls}>
        <MetaProvider
          meta={
            items && items.length
              ? {
                  [META_ENUM.METATYPE.SCRIPT_LD_JSON_ARTISTS_LIST]: generateLDJSON(
                    LDTYPES.ARTISTS_LIST,
                    items.map((item) => {
                      return {
                        ...item,
                        url: UrlAssembler.artistProfile(item.id, item.alias, {
                          origin: LocationManager.get('origin'),
                        }),
                      };
                    }),
                  ),
                }
              : null
          }
          hasError={hasError}
        />
        <Wrapper
          sizes={{
            xs: 'wide',
            sm: 'wide',
            md: 'wide',
            lg: 'narrow',
            xl: 'narrow',
          }}
        >
          {items && items.length ? <CreateFollowProvider leadersList={items} /> : null}
          <ArtistListSection
            items={items}
            storeCode={storeCode}
            loading={loading || !storeCode}
            domain={domain}
            pageUrl={pageUrl}
            textSearchQuery={q}
            paginationType={paginationType}
            pagination={
              pagination
                ? {
                    totalPages: pagination.totalPages,
                    total: pagination.totalItems,
                    pageSize: pagination.itemsPerPage,
                    current: pagination.currentPage,
                  }
                : {}
            }
          />
        </Wrapper>
      </Section>
    </LayoutDefault>
  );
};

const HOCArtistListPage = HOCFilterPageRoute(ArtistList, FILTER_ENUM.domain.ARTIST);

export default function ArtistListPage(props: any) {
  const match = useRouteMatch();
  const { translatedLocation: location } = useUrlParams();

  return (
    <HOCArtistListPage
      {...props}
      match={match}
      location={location}
      paginationType={PAGINATION_ENUM.type.SLIDER}
      domain={FILTER_ENUM.domain[props.routeConfig.filterDomainKey]}
    />
  );
}
